// @givmo/app-admin — the AdminAuthBackend transport adapter.
//
// The SINGLE transport seam between the framework-agnostic @givmo/auth admin flows and the generated
// @givmo/api-client SDK. @givmo/auth owns the Firebase/backend ORCHESTRATION (rollback, persistence,
// popup-first OAuth, OTP, the routing decision); this adapter only maps each port method onto the
// real generated `sdk.*` op over the configured shared client. No auth logic lives here.
//
// ★ CRUX — company-status casing. @givmo/auth's `decideRoutingState` compares `company.status`
// against `ACTIVE_COMPANY_STATUS = 'active'` (LOWERCASE), but the backend `CompanyDetailedOut.status`
// is the capitalized `CompanyStatus` enum (`'Active' | 'Deactivated' | 'Pending'`). So `getCompany`
// MUST normalize the status to lowercase before returning it — otherwise an ACTIVE company misroutes
// to `pendingManualVerification`. (Verified against packages/api-client generated types.gen.ts.)

import { sdk, firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';
import type {
  AdminAuthBackend,
  CompanyJoinTarget,
  CompanyRegistration,
  CompanyRegistrationResult,
  CompanyStatus,
  OnboardingResolution,
} from '@givmo/auth';
import type { Auth } from 'firebase/auth';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { ensureBackendUser } from '../../auth/ensure-backend-user';

/** The stable machine `error_code` the backend attaches to the EP-SUITE2 create-path join pivot. */
const COMPANY_DOMAIN_JOINABLE = 'COMPANY_DOMAIN_JOINABLE';

/**
 * Tolerantly map a `{ company_id, name, logo_url }` bag (from the resolve response's `company`, or the
 * COMPANY_DOMAIN_JOINABLE 409's `context.company`) onto a {@link CompanyJoinTarget}. Returns `null`
 * when the required id/name are missing or non-string — the caller then declines to pivot and lets the
 * normal error path proceed (fail-safe: never invent a half-formed request-access target).
 */
function toJoinTarget(raw: unknown): CompanyJoinTarget | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const companyId = typeof r.company_id === 'string' ? r.company_id : null;
  const name = typeof r.name === 'string' ? r.name : null;
  if (companyId === null || companyId.length === 0 || name === null) return null;
  const logoUrl = typeof r.logo_url === 'string' && r.logo_url.length > 0 ? r.logo_url : null;
  return { companyId, name, logoUrl };
}

/**
 * Throw the generated op's `error` when the call failed, else return its `data`. The generated SDK
 * returns `{ data, error }` (it does NOT throw by default). Throwing on `error` is what surfaces a
 * refused company registration to the sign-up (`use-register-company.ts`, which keeps the verified
 * account and returns to the details screen) and drives the OTP-confirm failure branch; the thrown
 * value is the api-client `GivmoError`.
 */
function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined && result.error !== null) {
    throw result.error;
  }
  if (result.data === undefined || result.data === null) {
    throw new Error('Empty response from the backend.');
  }
  return result.data;
}

/**
 * Resolve the Firebase user's email-verified state: reload the Auth user and read the STANDARD
 * `emailVerified` flag. This is a FIREBASE-side check (not a backend call) — the OTP confirm endpoint
 * sets the flag on the Firebase record, so a reload picks it up.
 *
 * ★ NEVER the `emailVerified` CUSTOM CLAIM (NF-FIX20). The webapp port fell back to it, but the confirm
 * merges that claim in as a persistent `true` that nothing ever clears: since NF-FIX19 an email change
 * clears the standard flag (the new address is unproven) and leaves the claim saying `true`, so the
 * fallback would route an account whose current address nobody proved as verified. The standard flag
 * is the one the backend's own gates read.
 */
async function resolveEmailVerified(auth: Auth): Promise<boolean> {
  const user = auth.currentUser;
  if (user === null) return false;
  await user.reload();
  const refreshed = auth.currentUser;
  if (refreshed === null) return false;
  return refreshed.emailVerified === true;
}

/**
 * Build the {@link AdminAuthBackend} the admin auth flows consume, wired to the generated SDK + the
 * live Firebase `auth` (for the email-verified reload). Constructed once and held in the auth context.
 */
export function createAdminAuthBackend(auth: Auth): AdminAuthBackend {
  return {
    // GET /admin/users/email_available?email=… → CheckEmailOut { available, email, message }.
    async checkEmailAvailable(email: string) {
      const data = unwrap(await sdk.adminCheckEmailAvailable({ query: { email } }));
      return { available: data.available, message: data.message };
    },

    // POST /admin/company — register the company. Resolves to a CompanyRegistrationResult:
    // `{ outcome: 'created' }` normally, or `{ outcome: 'requestAccess', company }` when the EP-SUITE2
    // pivot is ON and the backend signals the caller's work-email domain already belongs to an ACTIVE
    // company (the structured COMPANY_DOMAIN_JOINABLE 409). Returning the pivot INSTEAD of throwing is
    // exactly what keeps @givmo/auth from rolling the Firebase user back. Flag-off — or any other
    // error — THROWS exactly as before (byte-identical: the rollback still fires). Body is the
    // CompanyRegister shape { name, number_of_employees }; first/last name ride the Firebase
    // displayName set by adminSignUpWithEmail before this call.
    async registerCompany(params: CompanyRegistration): Promise<CompanyRegistrationResult> {
      const { data, error } = await sdk.companiesCreateCompany({
        body: { name: params.companyName, number_of_employees: params.numberOfEmployees },
      });
      if (error !== undefined && error !== null) {
        if (
          isCompanyJoinOrCreateEnabled() &&
          isGivmoError(error) &&
          firstErrorCode(error) === COMPANY_DOMAIN_JOINABLE
        ) {
          const company = toJoinTarget(firstErrorContext(error)?.company);
          if (company !== null) return { outcome: 'requestAccess', company };
        }
        throw error;
      }
      if (data === undefined || data === null) {
        throw new Error('Empty response from the backend.');
      }
      return { outcome: 'created' };
    },

    // POST /admin/company/resolve — resolve the onboarding branch for a caller with a VERIFIED work
    // email (EP-SUITE2). Throws (typically a 403) when the email is not yet server-verified, so the
    // caller falls through to the create path. Maps the wire `company` (snake) onto the camelCase
    // target; a malformed company yields `company: null` rather than a half-formed target.
    async resolveDomain(): Promise<OnboardingResolution> {
      const data = unwrap(await sdk.companiesResolveCompanyOnboardingRoute());
      return {
        outcome: data.outcome,
        reason: data.reason,
        company: data.company ? toJoinTarget(data.company) : null,
      };
    },

    // GET /users/me → POST /users — the signed-in account's Givmo row (NF-FIX21: the email sign-up
    // provisions it right after creating the account, before the code and before any company). The
    // same per-uid bootstrap the OTP send runs (`ensure-backend-user.ts`), so the send that follows
    // repeats nothing; a refusal (an address already on a Givmo account) THROWS, and the sign-up
    // rolls the new account back.
    async provisionUser() {
      const user = auth.currentUser;
      if (user === null) throw new Error('Your account could not be confirmed.');
      await ensureBackendUser(user.uid, user.email);
    },

    // POST /users/me/email_verification — trigger the OTP email. Success is a documented `200` with a
    // JSON `null` body, so treat any non-error result as success (error-only, NOT `unwrap`): a bare
    // `unwrap` false-throws "Empty response from the backend." on the happy path, and the OTP screen
    // fires this on mount — surfacing a pink banner before the user has typed anything.
    async sendVerificationEmail() {
      const { error } = await sdk.usersSendEmailVerification();
      if (error !== undefined && error !== null) throw error;
    },

    // POST /users/me/confirm_email?verification_code=… — confirm the OTP (query param, NOT body).
    // Throws on an invalid code (→ @givmo/auth surfaces the wrong-code error).
    async confirmEmailCode(code: string) {
      unwrap(await sdk.usersConfirmUserEmail({ query: { verification_code: code } }));
    },

    // GET /admin/company → CompanyDetailedOut. `null` when the lookup fails / no company exists (the
    // routing decision treats a null company as "no company found"). ★ Normalize the capitalized
    // backend status to lowercase so it matches @givmo/auth's lowercase ACTIVE_COMPANY_STATUS.
    async getCompany(): Promise<CompanyStatus | null> {
      const { data, error } = await sdk.companiesGetCompany();
      if (error !== undefined && error !== null) return null;
      if (data === undefined || data === null) return null;
      return { status: data.status.toLowerCase() };
    },

    // Firebase-side email-verified check (webapp `isEmailVerified`).
    async isEmailVerified(): Promise<boolean> {
      return resolveEmailVerified(auth);
    },
  };
}
